import { useEffect, useState } from "react";

import { secondNftImageUrl } from "../../../services/image";
import { SecondNftForGallery } from "../../../types/nftType";

import { useSecondNftsLazyQuery } from "../../../libs/schema";
import EditLog from "../EditLog";

import Image from "next/image";
import { useMedia } from "react-use";
import { MOBILE_MAX_WIDTH } from "../../../constant";
import { GeneItemDescription } from "../GeneDetail/GeneItemDescription";
import styles from "../GeneDetail/geneDetail.module.scss";

type Props = {
  selectedGene: SecondNftForGallery;
  selectedGeneIndex: number;
  geneList: Array<SecondNftForGallery>;
  isMyGene: boolean;
  isUpdateGene: boolean;
  isBeforeReveal: boolean;
  onClickCloseBtn: () => void;
  handleEdit: (tokenId: string, answer: string, credit: string) => void;
};

const SecondGeneDetail = ({
  selectedGene,
  selectedGeneIndex,
  geneList,
  isMyGene,
  isUpdateGene,
  isBeforeReveal,
  onClickCloseBtn,
  handleEdit,
}: Props) => {
  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);
  // 表示するGENE
  const [currentGene, setCurrentGene] =
    useState<SecondNftForGallery>(selectedGene);

  // 表示するGENE_INDEX
  const [currentGeneIndex, setCurrentGeneIndex] =
    useState<number>(selectedGeneIndex);

  // answer text
  const [answerText, setAnswerText] = useState("");

  const onChangeAnswerText = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setAnswerText(event.target.value);
  };

  // credit text
  const [creditText, setCreditText] = useState("");

  const onChangeCreditText = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setCreditText(event.target.value);
  };

  const [isAnswerLocked, setIsAnswerLocked] = useState(false);

  // GENE詳細取得
  const [
    fetchSecondNfts,
    {
      /* loading, error, data */
    },
  ] = useSecondNftsLazyQuery();

  const doFetchSecondNft = async (
    tokenId: string
  ): Promise<SecondNftForGallery | null> => {
    const { data } = await fetchSecondNfts({
      variables: {
        secondNftFilterInput: {
          tokenId: Number(tokenId),
        },
      },
    });
    const res = data?.secondNfts.edges;
    return res && res.length ? res[0].node : null;
  };

  const settingGeneMethod = () => {
    if (isMyGene) {
      if (!selectedGene.tokenId) {
        return;
      }
      doFetchSecondNft(selectedGene.tokenId).then((gene) => {
        if (gene == null) {
          return;
        }
        setCurrentGene(gene);
      });
    } else {
      setCurrentGene(selectedGene);
    }
  };

  // EDITボタンを押した時
  const onClickEdit = (tokenId: string, answer: string, credit: string) => {
    // 文字のバリデーション: 全角チェック
    const regex = /^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/;
    if (!answer.match(regex) || !credit.match(regex)) {
      alert(
        "英語・数字（半角）のみ入力してください。\nOnly English and numbers (half-width characters)."
      );
      return;
    }

    // 回答書き込み
    handleEdit(tokenId, answer, credit);
  };

  // propsのgeneをwatch: MYGENEの時は初期表示時も対象のGENE詳細取得
  useEffect(() => {
    settingGeneMethod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGene]);

  useEffect(() => {
    setAnswerText(currentGene?.answer ?? "");
    setCreditText(currentGene?.credit ?? "");
  }, [currentGene]);

  // 回答書き込み後の更新
  useEffect(() => {
    if (isUpdateGene) {
      settingGeneMethod();
      setIsAnswerLocked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateGene]);

  useEffect(() => {
    setCurrentGeneIndex(selectedGeneIndex);
  }, [selectedGeneIndex]);

  /**
   * 表示するGENEを設定する
   * @param index ターゲットのGENEのインデックス
   * @param isBefore 前のGENEを表示するか後のGENEを表示するか
   */
  const setTargetGene = async (index: number, isBefore: boolean) => {
    if (!geneList[index]) {
      return;
    }
    // MYGENEの時は詳細をとりなおす
    if (isMyGene) {
      if (!geneList[index].tokenId) {
        return;
      }
      doFetchSecondNft(geneList[index].tokenId!).then((gene) => {
        if (gene == null) {
          return;
        }
        setAnswerText("");
        setCreditText("");
        setCurrentGene(gene);
        setCurrentGeneIndex(index);
      });
    } else {
      setAnswerText("");
      setCreditText("");
      setCurrentGene(geneList[index]);
      setCurrentGeneIndex(index);
    }
  };

  // 左ボタン
  const onClickLeftBtn = () => {
    let targetIndex: number = currentGeneIndex - 1; // ひとつ前
    // 前がない
    if (targetIndex < 0) {
      return;
    }
    // 表示するGENEを設定
    setTargetGene(targetIndex, true);
  };

  const onClickRightBtn = () => {
    let targetIndex: number = currentGeneIndex + 1; // ひとつ後
    // 後がない
    if (targetIndex > geneList.length) {
      return;
    }
    // 表示するGENEを設定
    setTargetGene(targetIndex, false);
  };

  const ArrowBtns = () => (
    <>
      <div className={styles.detailItemLeftBtn} onClick={onClickLeftBtn}>
        <Image
          src="/images/button_images/arrow_left.png"
          alt="left"
          width={isMobile ? 30.15 : 80}
          height={isMobile ? 60 : 160}
          objectFit="contain"
        />
      </div>
      <div className={styles.detailItemRightBtn} onClick={onClickRightBtn}>
        <Image
          src="/images/button_images/arrow_right.png"
          alt="right"
          width={isMobile ? 30.15 : 80}
          height={isMobile ? 60 : 160}
          objectFit="contain"
        />
      </div>
    </>
  );

  return (
    <div
      className={styles.detailModalContainer}
      onClick={(e) => e.stopPropagation()}
    >
      <ArrowBtns />
      {currentGene && (
        <div className={styles.inner}>
          <div className={styles["inner-detail"]}>
            <div className={styles.geneItemImage}>
              <div className={styles["gene-image-inner"]}>
                <div className={`${styles.geneDetailItem} relative`}>
                  {/* eslint-disable-next-line */}
                  <img
                    src={secondNftImageUrl(currentGene.imageId)}
                    alt="NEN Nascent"
                    className="w-full relative"
                  />
                </div>
              </div>
            </div>
            {!isMobile && (
              <GeneItemDescription
                isMyGene={isMyGene}
                currentGene={currentGene}
                onClickCloseBtn={onClickCloseBtn}
                isMobile={isMobile}
                answerText={answerText}
                onChangeAnswerText={onChangeAnswerText}
                creditText={creditText}
                onChangeCreditText={onChangeCreditText}
                onClickEdit={onClickEdit}
                openSeaLinkPrefix={`${process.env.NEXT_PUBLIC_NASCENT_OPENSEA_ADDRESS}${process.env.NEXT_PUBLIC_NEN_NASCENT_TOKEN_ADDRESS}/`}
                showsPcEditBtn={!isMobile}
                showsMobileEditBtn={isMobile}
                attrs={attrs}
              >
                gene_type: {currentGene.geneType}
              </GeneItemDescription>
            )}
          </div>
          <div className={styles["sp-gene-description"]}>
            {isMobile && (
              <GeneItemDescription
                isMyGene={isMyGene}
                currentGene={currentGene}
                onClickCloseBtn={onClickCloseBtn}
                isMobile={isMobile}
                answerText={answerText}
                onChangeAnswerText={onChangeAnswerText}
                creditText={creditText}
                onChangeCreditText={onChangeCreditText}
                onClickEdit={onClickEdit}
                openSeaLinkPrefix={`${process.env.NEXT_PUBLIC_NASCENT_OPENSEA_ADDRESS}${process.env.NEXT_PUBLIC_NEN_NASCENT_TOKEN_ADDRESS}/`}
                showsPcEditBtn={!isMobile}
                showsMobileEditBtn={isMobile}
                attrs={attrs}
              >
                gene_type: {currentGene.geneType}
              </GeneItemDescription>
            )}
            {isMyGene ? (
              <div className={styles.detailModalSupplement}>
                <p>
                  ※The information you have entered may be rewritten after
                  resale.
                </p>
                <p>
                  ※The maximum number of characters that can be written is 20.
                </p>
              </div>
            ) : (
              <div className={styles.detailModalSupplement}>
                <p>
                  ※You can rewrite the &quot;ANSWER&quot; by purchasing this NFT
                  at Opensea.
                </p>
              </div>
            )}
            <EditLog
              answerUpdateHistories={currentGene.secondAnswerUpdateHistories}
            ></EditLog>
          </div>
        </div>
      )}
    </div>
  );
};

const attrs: [string, keyof SecondNftForGallery][] = [
  ["SPECIAL", "attributeSpecial"],
  ["HAIR_FRONT", "attributeHairFront"],
  ["EAR", "attributeEar"],
  ["NECK", "attributeNeck"],
  ["COSTUME", "attributeCostume"],
  ["FACE", "attributeFace"],
  ["MOUTH", "attributeMouth"],
  ["EYE", "attributeEye"],
  ["SKIN", "attributeSkin"],
  ["HAIR_BACK", "attributeHairBack"],
  ["BACKGROUND", "attributeBackground"],
];

export default SecondGeneDetail;
